import { DropdownItem } from '@bugbug/core/components/Dropdown';
import Icon from '@bugbug/core/components/Icon';
import { isRunningStatus } from '@bugbug/core/types/base';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Row } from 'react-table';

import type { TestRun } from '@bugbug/core/types/tests';
import type { SideEffect } from '@bugbug/core/types/utils';
import { VisibleOnRowHover } from '~/components/Table';
import { MISSING_UUID } from '~/constants/shared';
import useAppRoutes from '~/hooks/useAppRoutes';
import useModal from '~/hooks/useModal';
import { useAppDispatch } from '~/modules/store';
import { TestRunActions } from '~/modules/testRun/testRun.redux';

import { Container, Dropdown, IconButton } from './TestRunsActions.styled';

export type TestRunsActionsProps = {
  row: Row<TestRun>;
  onDelete: SideEffect;
};

export const TestRunsActions = ({ row, onDelete }: TestRunsActionsProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const modal = useModal();
  const testRun = row.original;
  const isRunning = isRunningStatus(testRun.status);
  const [areActionsLocked, setAreActionsLocked] = useState(false);
  const appRoutes = useAppRoutes('testRunsList');

  const handleStopRunning = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      dispatch(TestRunActions.stopRequest([testRun.id]));
    },
    [dispatch, testRun.id],
  );

  const handleClearClick = useCallback(() => {
    modal.show('delete_test_runs', {
      onDelete,
      testRunsIds: [testRun.id],
    });
  }, [modal, onDelete, testRun.id]);

  const handleGoToTest = () => {
    appRoutes.push('test', {
      testId: testRun.testSourceId ?? MISSING_UUID,
    });
  };

  const lockActionButtons = () => setAreActionsLocked(true);
  const unlockActionButtons = () => setAreActionsLocked(false);

  return (
    <Container data-testid="TestRunsActions">
      {isRunning && (
        <VisibleOnRowHover force={isRunning || areActionsLocked}>
          <IconButton data-testid="TestRunsActions.StopButton" onClick={handleStopRunning}>
            <Icon name="stop" size={12} />
          </IconButton>
        </VisibleOnRowHover>
      )}
      <VisibleOnRowHover force={areActionsLocked}>
        <Dropdown
          variant="icon"
          anchor="bottom-end"
          iconName="more"
          onOpen={lockActionButtons}
          onClose={unlockActionButtons}
          condensed
        >
          <DropdownItem onClick={handleGoToTest}>
            <Icon name="cornerDownRight" /> {t('testRunRowActions.goToTest.label', 'Go to test')}
          </DropdownItem>
          <DropdownItem onClick={handleClearClick} danger>
            <Icon name="delete" /> {t('testRunRowActions.remove.label', 'Clear from history')}
          </DropdownItem>
        </Dropdown>
      </VisibleOnRowHover>
    </Container>
  );
};
